import { getValueFromPropsPath } from "../nested-props.js";
import type { AsMutableArray, MatchMutableOrImmutable, MutableOrImmutableArray } from "../array-checks.js";
import type { NestedPropsPathValue } from "../nested-props.js";
import { type ApplyMergeObjects } from "../utils.js";
import type { UnwrapArrayType } from "./mapArrayToProp.js";
import { isPropertyKey } from "../primitive-types.js";

type BuildPropIndexedObjSplit<
  ARRAY extends MutableOrImmutableArray<any>,
  IndexByProp extends keyof T | PropertyKey,
  Result extends any[] = [],
  T extends (ARRAY extends MutableOrImmutableArray<infer U> ? U : never) = ARRAY extends MutableOrImmutableArray<infer U> ? U : never
> =
  IndexByProp extends keyof T
  ? (
    AsMutableArray<ARRAY> extends infer MUTABLE_ARRAY
    ? MatchMutableOrImmutable<
      ARRAY,
      MUTABLE_ARRAY extends [ infer FirstItem extends T, ...infer Remaining extends T[] ]
      ? (
        IndexByProp extends keyof FirstItem
        ? FirstItem[ IndexByProp ] extends PropertyKey
        ? BuildPropIndexedObjSplit<Remaining, IndexByProp, [ ...Result, { [ K in FirstItem[ IndexByProp ] ]: FirstItem } ]>
        : never
        : never
      ) :
      MUTABLE_ARRAY extends [] ? Result :
      MUTABLE_ARRAY extends (infer U)[]
      ? (
        IndexByProp extends keyof U
        ? U[ IndexByProp ] extends PropertyKey
        ? { [ K in IndexByProp ]: U }
        : never
        : never
      )
      : Result
    > : never
  ) :
  never;

export type ConvertArrayToPropIndexedObject<
  ARRAY extends MutableOrImmutableArray<any>,
  IndexByProp extends keyof T | PropertyKey,
  T extends (ARRAY extends MutableOrImmutableArray<infer U> ? U : never) = ARRAY extends MutableOrImmutableArray<infer U> ? U : never
> = ApplyMergeObjects<BuildPropIndexedObjSplit<ARRAY, IndexByProp, [], T>>;



type BuildDeepPropIndexedObjSplit<
  ARRAY extends MutableOrImmutableArray<any>,
  IndexByPath extends [ keyof T | PropertyKey, ...PropertyKey[] ],
  Result extends any[] = [],
  T extends (ARRAY extends MutableOrImmutableArray<infer U> ? U : never) = ARRAY extends MutableOrImmutableArray<infer U> ? U : never
> =
  AsMutableArray<ARRAY> extends infer MUTABLE_ARRAY
  ? MatchMutableOrImmutable<
    ARRAY,
    MUTABLE_ARRAY extends [ infer FirstItem extends T, ...infer Remaining extends T[] ]
    ? (
      NestedPropsPathValue<FirstItem, IndexByPath> extends infer NestedValue extends PropertyKey
      ? (
        BuildDeepPropIndexedObjSplit<
          Remaining,
          IndexByPath,
          [ ...Result, { [ K in NestedValue ]: FirstItem } ]
        >
      )
      : NestedPropsPathValue<FirstItem, IndexByPath> extends infer NestedValue extends PropertyKey[]
      ? (
        BuildDeepPropIndexedObjSplit<
          Remaining,
          IndexByPath,
          [ ...Result, { [ K in NestedValue[ number ] ]: FirstItem } ]
        >
      )
      : never
    ) :
    Result
  > : never;

export type ConvertArrayToDeepPropIndexedObject<
  ARRAY extends MutableOrImmutableArray<any>,
  IndexByPath extends [ keyof T | PropertyKey, ...PropertyKey[] ],
  T extends (ARRAY extends MutableOrImmutableArray<infer U> ? U : never) = ARRAY extends MutableOrImmutableArray<infer U> ? U : never
> = ApplyMergeObjects<BuildDeepPropIndexedObjSplit<ARRAY, IndexByPath, [], T>>;


export function convertArrayToPropIndexedObject<
  const ARRAY extends any[],
  const IndexByProp extends keyof T,
  T extends UnwrapArrayType<ARRAY, any> = UnwrapArrayType<ARRAY, any>
> (array: ARRAY, indexBy: IndexByProp): ConvertArrayToPropIndexedObject<ARRAY, IndexByProp>;

export function convertArrayToPropIndexedObject<
  const ARRAY extends any[],
  const IndexByProp extends PropertyKey
> (array: ARRAY, indexBy: IndexByProp): ConvertArrayToPropIndexedObject<ARRAY, IndexByProp>;

export function convertArrayToPropIndexedObject<
  const ARRAY extends any[],
  const IndexByProp extends keyof T | PropertyKey,
  T extends UnwrapArrayType<ARRAY, any> = UnwrapArrayType<ARRAY, any>
> (array: ARRAY, indexBy: IndexByProp): ConvertArrayToPropIndexedObject<ARRAY, IndexByProp> {
  const propIndexedObj = array.reduce((result, item) => {
    const propValue = item[indexBy];
    return Object.assign(result, { [propValue]: item });
  }, {});
  return propIndexedObj;
}

export function convertArrayToDeepPropIndexedObject<
  const ARRAY extends any[],
  const IndexByPath extends [ keyof T, ...PropertyKey[] ],
  T extends UnwrapArrayType<ARRAY, any> = UnwrapArrayType<ARRAY, any>
> (array: ARRAY, indexByPath: IndexByPath): ConvertArrayToDeepPropIndexedObject<ARRAY, IndexByPath>;

export function convertArrayToDeepPropIndexedObject<
  const ARRAY extends any[],
  const IndexByPath extends [ PropertyKey, ...PropertyKey[] ]
> (array: ARRAY, indexByPath: IndexByPath): ConvertArrayToDeepPropIndexedObject<ARRAY, IndexByPath>;

export function convertArrayToDeepPropIndexedObject<
  const ARRAY extends any[],
  const IndexByPath extends [ keyof T | PropertyKey, ...PropertyKey[] ],
  T extends UnwrapArrayType<ARRAY, any> = UnwrapArrayType<ARRAY, any>
> (array: ARRAY, indexByPath: IndexByPath): ConvertArrayToDeepPropIndexedObject<ARRAY, IndexByPath> {
  return array.reduce((result, item) => {
    const propValue = getValueFromPropsPath(item, indexByPath, []);
    if (Array.isArray(propValue)) {
      return Object.assign(result, propValue.reduce((innerResult, key) => {
        return Object.assign(innerResult, { [key]: item });
      }, {}));
    }
    else {
      if (isPropertyKey(propValue)) {        
        return Object.assign(result, { [propValue]: item });
      }
      else {
        throw new Error("Expected property key!");
      }
    }
  }, {});
}